import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, User, FileSignature, FolderOpen, Loader2, Mail, Phone, MapPin, CreditCard, IdCard, Cake, Save, Percent } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { ContractUploader, FileUploader, deleteContractRow, deleteFileRow, SimpleList } from "./DriveArtistDetail";
import { deleteCreator } from "../lib/driveClient";
import { DeleteEntityButton } from "../components/DeleteEntityButton";

const TABS = [
  { id: "overview", label: "Übersicht", icon: User },
  { id: "contracts", label: "Verträge", icon: FileSignature },
  { id: "files", label: "Dateien", icon: FolderOpen },
];

export default function DriveCreatorDetail() {
  const { id } = useParams();
  const [creator, setCreator] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);
  const [dat, setDat] = useState({ contracts: [], files: [] });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [a, c, f] = await Promise.all([
        supabase.from("drive_creators").select("*").eq("id", id).single(),
        supabase.from("drive_contracts").select("*").eq("creator_id", id).order("created_at", { ascending: false }),
        supabase.from("drive_files").select("*").eq("creator_id", id).order("uploaded_at", { ascending: false }).then(r => r).catch(() => ({ data: [] })),
      ]);
      if (a.error) throw a.error;
      setCreator(a.data); setEdit(a.data);
      setDat({ contracts: c.data || [], files: f.data || [] });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const saveCreator = async () => {
    setSaving(true);
    try {
      const patch = {
        creator_name: edit.creator_name, first_name: edit.first_name, last_name: edit.last_name,
        email: edit.email, phone: edit.phone, address: edit.address, zip_city: edit.zip_city,
        birthdate: edit.birthdate || null, tax_id: edit.tax_id, iban: edit.iban, bic: edit.bic,
        bank_name: edit.bank_name, commission_percent: Number(edit.commission_percent) || 30,
        notes: edit.notes,
      };
      const { error } = await supabase.from("drive_creators").update(patch).eq("id", id);
      if (error) throw error;
      toast.success("Gespeichert"); await loadAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading || !creator) return <div className="flex items-center gap-3 text-zinc-400 text-sm py-16"><Loader2 size={16} className="animate-spin" /> Lade…</div>;
  const displayName = [creator.first_name, creator.last_name].filter(Boolean).join(" ") || creator.creator_name;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/drive/crm" className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white font-bold mb-4"><ArrowLeft size={12} /> Zurück zum CRM</Link>
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-2">Creator</div>
          <div className="font-display font-black uppercase text-3xl md:text-4xl mb-2">{creator.creator_name || displayName}</div>
          <div className="text-sm text-zinc-400">{displayName}{creator.customer_number ? ` · ${creator.customer_number}` : ""} · Management-Anteil {creator.commission_percent ?? 30} %</div>
        </div>
        <div className="flex justify-end mt-4">
          <DeleteEntityButton
            label="Creator löschen"
            entityName={creator.creator_name || displayName}
            onConfirm={(withFiles) => deleteCreator(id, { withFiles })}
          />
        </div>
      </div>
      <div className="flex gap-1 border-b border-white/[0.06] overflow-x-auto">
        {TABS.map((t) => { const Icon = t.icon; const active = tab === t.id;
          return (<button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-4 py-3 text-[10px] tracking-[0.3em] uppercase font-bold border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#ff0033] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}><Icon size={13} /> {t.label}</button>);
        })}
      </div>
      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field icon={<User size={13} />} label="Creator-Name" value={edit.creator_name} onChange={(v) => setEdit({ ...edit, creator_name: v })} />
          <Field icon={<User size={13} />} label="Vorname" value={edit.first_name} onChange={(v) => setEdit({ ...edit, first_name: v })} />
          <Field icon={<User size={13} />} label="Nachname" value={edit.last_name} onChange={(v) => setEdit({ ...edit, last_name: v })} />
          <Field icon={<Cake size={13} />} label="Geburtsdatum" type="date" value={edit.birthdate || ""} onChange={(v) => setEdit({ ...edit, birthdate: v })} />
          <Field icon={<Mail size={13} />} label="E-Mail" value={edit.email || ""} onChange={(v) => setEdit({ ...edit, email: v })} />
          <Field icon={<Phone size={13} />} label="Telefon" value={edit.phone || ""} onChange={(v) => setEdit({ ...edit, phone: v })} />
          <Field icon={<MapPin size={13} />} label="Adresse" value={edit.address || ""} onChange={(v) => setEdit({ ...edit, address: v })} />
          <Field icon={<MapPin size={13} />} label="PLZ / Ort" value={edit.zip_city || ""} onChange={(v) => setEdit({ ...edit, zip_city: v })} />
          <Field icon={<IdCard size={13} />} label="Steuer-ID" value={edit.tax_id || ""} onChange={(v) => setEdit({ ...edit, tax_id: v })} />
          <Field icon={<Percent size={13} />} label="Management-Anteil (%)" type="number" value={edit.commission_percent ?? 30} onChange={(v) => setEdit({ ...edit, commission_percent: v })} />
          <Field icon={<CreditCard size={13} />} label="IBAN" value={edit.iban || ""} onChange={(v) => setEdit({ ...edit, iban: v })} />
          <Field icon={<CreditCard size={13} />} label="BIC" value={edit.bic || ""} onChange={(v) => setEdit({ ...edit, bic: v })} />
          <Field icon={<CreditCard size={13} />} label="Bank" value={edit.bank_name || ""} onChange={(v) => setEdit({ ...edit, bank_name: v })} />
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Notizen</label>
            <textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={3} className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
          </div>
          <div className="md:col-span-2">
            <button onClick={saveCreator} disabled={saving} className="inline-flex items-center gap-2 bg-[#ff0033] text-white px-6 py-3 text-[10px] font-bold tracking-[0.3em] uppercase hover:shadow-[0_0_20px_rgba(255,0,51,0.5)] transition-all disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}{saving ? "Speichere…" : "Änderungen speichern"}
            </button>
          </div>
        </div>
      )}
      {tab === "contracts" && (
        <>
          <ContractUploader entityType="creator" entityId={id} onDone={loadAll} />
          <SimpleList rows={dat.contracts.map(c => ({ id: c.id, primary: c.title || c.category, secondary: (c.status || "").toUpperCase(), date: c.created_at, path: c.signed_storage_path || c.storage_path }))} onDelete={async (r) => { await deleteContractRow(r); toast.success("Gelöscht"); await loadAll(); }} empty="Keine Verträge." />
        </>
      )}
      {tab === "files" && (
        <>
          <FileUploader entityType="creator" entityId={id} onDone={loadAll} />
          <SimpleList rows={dat.files.map(f => ({ id: f.id, primary: f.name, secondary: `${((f.size || 0) / 1024).toFixed(0)} KB`, date: f.uploaded_at, path: f.storage_path }))} onDelete={async (r) => { await deleteFileRow(r); toast.success("Gelöscht"); await loadAll(); }} empty="Keine Dateien." />
        </>
      )}
    </div>
  );
}

const Field = ({ icon, label, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2 flex items-center gap-2">{icon} {label}</label>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-white outline-none focus:border-[#ff0033]/60" />
  </div>
);
